export type MeasurementUnit = 'Metric' | 'Imperial'
export type MeasurementKind = 'distance' | 'elevation' | 'weight' | 'grade'

type MeasurementDefinition = {
  metricUnit: string
  imperialUnit: string
  toImperialFactor: number
  metricFractionDigits: number
  imperialFractionDigits: number
  metricInputStep: number
  imperialInputStep: number
  metricPrecision: number
}

export const MEASUREMENT_DEFINITIONS: Record<MeasurementKind, MeasurementDefinition> = {
  distance: {
    metricUnit: 'km',
    imperialUnit: 'mi',
    toImperialFactor: 0.621371,
    metricFractionDigits: 1,
    imperialFractionDigits: 1,
    metricInputStep: 0.1,
    imperialInputStep: 0.1,
    metricPrecision: 3
  },
  elevation: {
    metricUnit: 'm',
    imperialUnit: 'ft',
    toImperialFactor: 3.28084,
    metricFractionDigits: 0,
    imperialFractionDigits: 0,
    metricInputStep: 10,
    imperialInputStep: 50,
    metricPrecision: 0
  },
  weight: {
    metricUnit: 'kg',
    imperialUnit: 'lb',
    toImperialFactor: 2.20462,
    metricFractionDigits: 1,
    imperialFractionDigits: 1,
    metricInputStep: 0.5,
    imperialInputStep: 1,
    metricPrecision: 3
  },
  grade: {
    metricUnit: 'm/km',
    imperialUnit: 'ft/mi',
    // (3.28084 ft/m) / (0.621371 mi/km)
    toImperialFactor: 5.27998,
    metricFractionDigits: 0,
    imperialFractionDigits: 0,
    metricInputStep: 5,
    imperialInputStep: 25,
    metricPrecision: 0
  }
}

const roundTo = (value: number, fractionDigits: number) => {
  const factor = 10 ** fractionDigits
  return Math.round((value + Number.EPSILON) * factor) / factor
}

const trimTrailingZeros = (value: string) => value.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')

const EXACT_IMPERIAL_TO_METRIC = {
  distance: 1.609344,
  elevation: 0.3048
} as const

export const isMeasurementUnit = (value: unknown): value is MeasurementUnit =>
  value === 'Metric' || value === 'Imperial'

export const normalizeMeasurementUnit = (value: unknown): MeasurementUnit =>
  isMeasurementUnit(value) ? value : 'Metric'

export const unitLabelFor = (kind: MeasurementKind, unit: MeasurementUnit) => {
  const definition = MEASUREMENT_DEFINITIONS[kind]
  return unit === 'Imperial' ? definition.imperialUnit : definition.metricUnit
}

export const inputStepFor = (kind: MeasurementKind, unit: MeasurementUnit) => {
  const definition = MEASUREMENT_DEFINITIONS[kind]
  return unit === 'Imperial' ? definition.imperialInputStep : definition.metricInputStep
}

export const fractionDigitsFor = (kind: MeasurementKind, unit: MeasurementUnit) => {
  const definition = MEASUREMENT_DEFINITIONS[kind]
  return unit === 'Imperial' ? definition.imperialFractionDigits : definition.metricFractionDigits
}

export const metricToDisplay = (valueMetric: number, kind: MeasurementKind, unit: MeasurementUnit) => {
  const definition = MEASUREMENT_DEFINITIONS[kind]
  return unit === 'Imperial' ? valueMetric * definition.toImperialFactor : valueMetric
}

export const displayToMetric = (valueDisplay: number, kind: MeasurementKind, unit: MeasurementUnit) => {
  const definition = MEASUREMENT_DEFINITIONS[kind]
  const metricValue = unit === 'Imperial' ? valueDisplay / definition.toImperialFactor : valueDisplay
  return roundTo(metricValue, definition.metricPrecision)
}

// Difficulty thresholds are defined in authored imperial units. Keep enough
// precision that converting 6/9/12 miles or 500/1000/3000 feet cannot move a
// value across a scoring boundary before it reaches the backend.
export const hikeInputToMetric = (
  valueDisplay: number,
  kind: 'distance' | 'elevation',
  unit: MeasurementUnit
) => {
  const metricValue = unit === 'Imperial' ? valueDisplay * EXACT_IMPERIAL_TO_METRIC[kind] : valueDisplay
  return roundTo(metricValue, 6)
}

export const metricToHikeInput = (
  valueMetric: number,
  kind: 'distance' | 'elevation',
  unit: MeasurementUnit
) => {
  const displayValue = unit === 'Imperial' ? valueMetric / EXACT_IMPERIAL_TO_METRIC[kind] : valueMetric
  return roundTo(displayValue, 6)
}

export const formatDisplayNumber = (value: number, fractionDigits: number) =>
  trimTrailingZeros(value.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }))

export const formatInputNumber = (value: number, fractionDigits: number) =>
  trimTrailingZeros(value.toFixed(fractionDigits))

export const formatMeasurement = (
  valueMetric: number | null | undefined,
  kind: MeasurementKind,
  unit: MeasurementUnit,
  options?: {
    empty?: string
    fractionDigits?: number
    unitLabel?: string
  }
) => {
  if (valueMetric === null || valueMetric === undefined || Number.isNaN(valueMetric)) {
    return options?.empty ?? '—'
  }

  const fractionDigits = options?.fractionDigits ?? fractionDigitsFor(kind, unit)
  const displayValue = metricToDisplay(valueMetric, kind, unit)
  return `${formatDisplayNumber(displayValue, fractionDigits)} ${options?.unitLabel ?? unitLabelFor(kind, unit)}`
}

export const formatMeasurementInputValue = (
  valueMetric: number | null | undefined,
  kind: MeasurementKind,
  unit: MeasurementUnit
) => {
  if (valueMetric === null || valueMetric === undefined || Number.isNaN(valueMetric)) {
    return ''
  }

  return formatInputNumber(metricToDisplay(valueMetric, kind, unit), fractionDigitsFor(kind, unit))
}

export const labelWithUnit = (label: string, kind: MeasurementKind, unit: MeasurementUnit) =>
  `${label} (${unitLabelFor(kind, unit)})`

export const parseMeasurementInput = (
  value: string,
  kind: MeasurementKind,
  unit: MeasurementUnit
) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }

  const parsed = Number(trimmed.replace(/,/g, ''))
  return Number.isFinite(parsed) && parsed >= 0 ? displayToMetric(parsed, kind, unit) : null
}
